import React from 'react';
import { CheckCircle, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { colors, spacing, typography, borders } from '../../../../tokens';
import { formatTxHash, getLayerZeroScanUrl } from '../utils/formatters';

interface BridgeStatusProps {
  isBridgeSuccess: boolean;
  isBridgeLoading: boolean;
  bridgeHash: `0x${string}` | undefined;
  bridgeError: Error | null;
}

/**
 * Component for displaying bridge transaction status
 * Shows success, loading, or error states with transaction links
 */
export const BridgeStatus: React.FC<BridgeStatusProps> = ({
  isBridgeSuccess,
  isBridgeLoading,
  bridgeHash,
  bridgeError,
}) => {
  if (!isBridgeLoading && !isBridgeSuccess && !bridgeError) {
    return null;
  }

  return (
    <div
      style={{
        padding: spacing.lg,
        backgroundColor: colors.background.secondary.default,
        borderRadius: borders.component.card.radius,
        border: `1px solid ${
          bridgeError
            ? colors.semantic.error
            : isBridgeSuccess
            ? colors.semantic.success
            : colors.border.default.default
        }`,
      }}
    >
      {/* Loading State */}
      {isBridgeLoading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
          <Loader2 size={24} color={colors.brand.primary.base} className="animate-spin" />
          <div>
            <div
              style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text.primary.default,
              }}
            >
              Bridging in progress...
            </div>
            <div
              style={{
                fontSize: typography.fontSize.sm,
                color: colors.text.secondary.default,
                marginTop: spacing.xs,
              }}
            >
              Please wait while your transaction is being processed
            </div>
          </div>
        </div>
      )}

      {/* Success State */}
      {isBridgeSuccess && bridgeHash && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: spacing.md }}>
          <CheckCircle size={24} color={colors.semantic.success} />
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                color: colors.semantic.success,
              }}
            >
              Bridge transaction sent!
            </div>
            <div
              style={{
                fontSize: typography.fontSize.sm,
                color: colors.text.secondary.default,
                marginTop: spacing.xs,
              }}
            >
              Your tokens are being bridged to BSC. This may take a few minutes.
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.sm,
                marginTop: spacing.md,
              }}
            >
              <a
                href={getLayerZeroScanUrl(bridgeHash)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                  fontSize: typography.fontSize.sm,
                  color: colors.brand.primary.base,
                  textDecoration: 'none',
                }}
              >
                <span>Track on LayerZero Scan</span>
                <ExternalLink size={14} />
              </a>
              <div
                style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.text.muted.default,
                  fontFamily: typography.fontFamily.mono,
                }}
              >
                Tx: {formatTxHash(bridgeHash)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {bridgeError && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: spacing.md }}>
          <AlertCircle size={24} color={colors.semantic.error} />
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                color: colors.semantic.error,
              }}
            >
              Transaction failed
            </div>
            <div
              style={{
                fontSize: typography.fontSize.sm,
                color: colors.text.secondary.default,
                marginTop: spacing.xs,
              }}
            >
              {bridgeError.message || 'An error occurred during the bridge transaction'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
